<template>
  <div class="fill-height">
    <line-chart
      v-if="viewMode === 'chart'"
      class="fill-height pb-0"
      type="bar"
      :height="220"
      :raw-data="accelerationZones"
      :chart-value-keys="[
        { key: $t('table_headers.decelerations'), type: 'bar'},
        { key: $t('table_headers.accelerations'), type: 'bar'}
      ]"
      :plot-options="{
        bar: {
          borderRadius: 5,
          borderRadiusApplication: 'end',
          borderRadiusWhenStacked: 'all',
          horizontal: true,
          barHeight: '60%',
          columnWidth: '90%',
        }
      }"
      x-value="key"
      :grid="{ show: false }"
      :xaxis="{
        categories: accelerationZones.map(stat => stat.label),
        tickPlacement: 'between',
        min: -maxValue,
        max: maxValue,
        axisBorder: { show: false },
        labels: {
          formatter: function(value) {
            return Math.round(Math.abs(value));
          }
        }
      }"
      :tooltip="{
        x: {
          formatter: function(value) {
            return value;
          }
        },
        y: {
          formatter: function(value) {
            return Math.abs(value);
          }
        }
      }"
      :stacked="true"
    >
      <template v-slot:header>
        <v-card-title class="pa-6 d-flex justify-space-between">
          <div class="subtitle-2 grey--text text--grey-100">
            {{ $t("card_title.acceleration_zone_breakdown") }}{{ aggregation ? ": " + $t(`variables.${aggregation}.long`) : '' }}
          </div>
          <v-btn
            v-if="removable"
            icon
            style="pointer-events: auto"
            @click="$emit('remove')"
          >
            <v-icon small>
              fal fa-times
            </v-icon>
          </v-btn>
          <visual-list-toggle
            v-else
            :value.sync="viewMode"
          />
        </v-card-title>
      </template>
    </line-chart>

    <v-card
      v-else-if="viewMode === 'table'"
      class="fill-height rounded-lg"
    >
      <v-card-title class="pa-6 d-flex justify-space-between">
        <div class="subtitle-2 grey--text text--grey-100">
          {{ $t("card_title.acceleration_zone_breakdown") }}{{ aggregation ? ": " + $t(`variables.${aggregation}.long`) : '' }}
        </div>
        <div class="d-flex align-center">
          <visual-list-toggle
            v-if="!removable"
            :value.sync="viewMode"
          />
        </div>
      </v-card-title>

      <v-data-table
        :headers="tableHeaders"
        :items="accelerationZones"
        hide-default-footer
        disable-pagination
        :items-per-page="-1"
        fixed-header
        dense
        :height="220"
      >
        <template v-slot:item.Decelerations="{ item }">
          {{ Math.abs(item.Decelerations) }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import LineChart from '@/components/charts/LineChart.vue'
import VisualListToggle from '@/components/buttons/VisualListToggle.vue'
import { hasSixZoneSetup } from '@/utils/helpers'

export default {
  name: 'AccelerationZoneChart',
  components: { LineChart, VisualListToggle },
  props: {
    stats: {
      type: Object,
      required: true
    },
    removable: {
      type: Boolean,
      default: false
    },
    aggregation: {
      type: String,
      default: null
    }
  },
  data: () => ({
    viewMode: 'chart'
  }),
  computed: {
    accelerationZones() {
      if (hasSixZoneSetup()) {
        return [
          {
            key: 'ACCZONE6',
            label: this.$t('variables.zone6'),
            Accelerations: this.getStatValue('ACCZONE6') || 0,
            Decelerations: -(this.getStatValue('DECZONE6') || 0)
          },
          {
            key: 'ACCZONE5',
            label: this.$t('variables.zone5'),
            Accelerations: this.getStatValue('ACCZONE5') || 0,
            Decelerations: -(this.getStatValue('DECZONE5') || 0)
          },
          {
            key: 'CAPGCSACB13EG2',
            label: this.$t('variables.zone4'),
            Accelerations: this.getStatValue('CAPGCSACB13EG2') || 0,
            Decelerations: -(this.getStatValue('CAPGCSDCB13EG2') || 0)
          },
          {
            key: 'UXACB68',
            label: this.$t('variables.zone3'),
            Accelerations: this.getStatValue('UXACB68') || 0,
            Decelerations: -(this.getStatValue('UXDCB13') || 0)
          },
          {
            key: 'UXACB58',
            label: this.$t('variables.zone2'),
            Accelerations: this.getStatValue('UXACB58') || 0,
            Decelerations: -(this.getStatValue('UXDCB14') || 0)
          }
        ]
      }
      return [
        {
          key: 'CAPGCSACB13EG2',
          label: this.$t('variables.zone4'),
          Accelerations: this.getStatValue('CAPGCSACB13EG2') || 0,
          Decelerations: -(this.getStatValue('CAPGCSDCB13EG2') || 0)
        },
        {
          key: 'UXACB68',
          label: this.$t('variables.zone3'),
          Accelerations: this.getStatValue('UXACB68') || 0,
          Decelerations: -(this.getStatValue('UXDCB13') || 0)
        },
        {
          key: 'UXACB58',
          label: this.$t('variables.zone2'),
          Accelerations: this.getStatValue('UXACB58') || 0,
          Decelerations: -(this.getStatValue('UXDCB14') || 0)
        }
      ]
    },
    maxValue() {
      return Math.max(
        ...this.accelerationZones.map(stat => stat.Accelerations),
        ...this.accelerationZones.map(stat => Math.abs(stat.Decelerations))
      ) + 1
    },
    tableHeaders() {
      return [
        {
          text: this.$t('table_headers.zone'),
          value: 'label',
          sortable: true,
          width: '40%'
        },
        {
          text: this.$t('table_headers.accelerations'),
          value: 'Accelerations',
          sortable: true,
          align: 'center'
        },
        {
          text: this.$t('table_headers.decelerations'),
          value: 'Decelerations',
          sortable: true,
          align: 'center'
        }
      ]
    }
  },
  methods: {
    getKeyWithAggregation(key) {
      return this.aggregation && !this.removable ? `${key}_${this.aggregation}` : key
    },
    getStatValue(key) {
      const aggregatedKey = this.getKeyWithAggregation(key)
      return this.stats[aggregatedKey]
    }
  }
}
</script>
